/**
 * CounterChoiceSelect — the one dropdown for assigning an officer to a counter, or to a free-text duty.
 *
 * Every counter dropdown on the live roster (the popover that opens on a cell, the officer card's rows)
 * is this component, so what they offer and how it looks — the "Counters" and "Non-counter deployments"
 * groups (gray headers, plain options), the italic gray look of a chosen duty, the amber look of a row
 * still waiting for a choice — is defined here and in utils/counterChoice, and nowhere else. Callers bring only their size
 * and spacing (`style`).
 *
 * A native <select>: it opens the platform's own picker (the wheel on iOS, the list on Android). Those
 * cannot colour single options, so the two kinds of choice are told apart by their group headers.
 */
import { forwardRef, useEffect, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { NUM_SLOTS, formatCounterLabel } from '@/config/modeConfig';
import { vacancyText, type CounterVacancy } from '@/utils/hwCounterVacancy';
import { useDutyOptions } from '@/utils/dutyOptions';
import {
  COUNTER_GROUP_LABEL, DUTY_FILL, DUTY_GROUP_LABEL, DUTY_INK, DUTY_LOOK, EMPTY_CHOICE_LOOK, GROUP_HEADER_LOOK,
  dutiesToOffer, dutyChoice, parseCounterChoice,
} from '@/utils/counterChoice';
import { UI_SCALE, popupMaxH, POPUP_MIN_FONT_PX } from '@/utils/uiScale';

interface Props {
  /** A counter number, "duty:<text>", or "" for none yet (see utils/counterChoice). */
  value: string;
  onChange: (choice: string) => void;
  numCounters: number;
  operationMode?: string;
  disabled?: boolean;
  /** Offered as a disabled first option while `value` is "" (e.g. "Select ctr"). */
  emptyLabel?: string;
  /** Size, spacing and font — the look of the choice itself is not the caller's to change. */
  style?: CSSProperties;
  'aria-label'?: string;
  'data-testid'?: string;
  /**
   * When given, the dropdown becomes a sheet of its own that colours each counter by whether it is free
   * (green: whole shift / until when) or not (red: who holds it until when) — a native <select> cannot
   * colour options on phones or Macs. Non-counter deployments stay gray. Same look on every device.
   */
  vacancyOf?: (counterNum: number) => CounterVacancy | undefined;
  /** HHMM of an exclusive end slot, for the vacancy lines. */
  slotTime?: (slot: number) => string;
  /** End slot of the range being assigned; a free run shorter than it gets an amber warning. */
  neededUntilSlot?: number;
  /** Sheet heading. */
  title?: string;
}

const GREEN = '#15803d';
const RED = '#b91c1c';
const AMBER = '#b45309';
const vacancyColour = (v: CounterVacancy | undefined) => (!v ? '#6b7280' : v.state === 'free' ? GREEN : RED);

function CounterChoiceSheet({
  value, onChange, numCounters, operationMode, disabled, emptyLabel, style, vacancyOf, slotTime, neededUntilSlot, title,
}: Props & { vacancyOf: NonNullable<Props['vacancyOf']>; slotTime: NonNullable<Props['slotTime']> }) {
  const [open, setOpen] = useState(false);
  const listed = useDutyOptions().map((d) => d.text);
  const currentDuty = parseCounterChoice(value).duty;
  const duties = dutiesToOffer(listed, currentDuty);
  const currentCounter = parseCounterChoice(value).counter;
  const narrow = typeof window !== 'undefined' && window.innerWidth < 640;

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const pick = (v: string) => { setOpen(false); if (v !== value) onChange(v); };
  const label = currentDuty ?? (currentCounter > 0 ? formatCounterLabel(currentCounter, operationMode) : (emptyLabel ?? 'Select ctr'));
  const v = currentCounter > 0 ? vacancyOf(currentCounter) : undefined;
  const look: CSSProperties = currentDuty !== undefined
    ? DUTY_LOOK
    : v ? { border: `1.5px solid ${vacancyColour(v)}`, color: vacancyColour(v) }
    : value === '' && !disabled ? EMPTY_CHOICE_LOOK : {};

  return (
    <>
      <button
        type="button"
        disabled={disabled}
        aria-haspopup="dialog"
        title={v && slotTime ? vacancyText(v, slotTime, NUM_SLOTS) : undefined}
        onClick={() => setOpen(true)}
        style={{ ...style, textAlign: 'left', position: 'relative', paddingRight: 16, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', ...look }}
      >
        {label}
        <span aria-hidden style={{ position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)', fontSize: POPUP_MIN_FONT_PX, opacity: 0.7 }}>▼</span>
      </button>
      {open && createPortal(
        <div
          role="dialog"
          aria-modal="true"
          aria-label={title ?? 'Choose counter'}
          onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
          // A portal still bubbles to its React parents: Enter / Escape here must not confirm or cancel the card behind.
          onKeyDown={(e) => e.stopPropagation()}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 4600,
            display: 'flex', justifyContent: 'center', alignItems: narrow ? 'flex-end' : 'center',
            padding: narrow ? 0 : 16, overscrollBehavior: 'contain',
          }}
        >
          <div style={{
            background: 'white', width: '100%', maxWidth: 440, zoom: UI_SCALE, maxHeight: popupMaxH(narrow ? 70 : 80, 'dvh'),
            display: 'flex', flexDirection: 'column', overflow: 'hidden',
            borderRadius: narrow ? '16px 16px 0 0' : 12, boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
          }}>
            <div style={{ padding: '14px 16px 10px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
              <div style={{ flex: 1, fontSize: 15, fontWeight: 700, color: '#1a1a2e' }}>{title ?? 'Choose counter'}</div>
              <button type="button" aria-label="Close" onClick={() => setOpen(false)}
                style={{ border: 'none', background: '#f1f5f9', borderRadius: 8, width: 36, height: 36, fontSize: 16, cursor: 'pointer', color: '#475569' }}>✕</button>
            </div>
            <div style={{ overflowY: 'auto', minHeight: 0, paddingBottom: 'env(safe-area-inset-bottom)' }}>
              <div style={{ ...GROUP_HEADER_LOOK, padding: '6px 16px', fontSize: 12 }}>{COUNTER_GROUP_LABEL}</div>
              {Array.from({ length: numCounters }, (_, i) => {
                const n = i + 1;
                const cv = vacancyOf(n);
                const selected = n === currentCounter && currentDuty === undefined;
                const short = cv?.state === 'free' && neededUntilSlot !== undefined && cv.untilSlot < neededUntilSlot;
                return (
                  <button key={n} type="button" onClick={() => pick(String(n))}
                    ref={selected ? (el) => el?.scrollIntoView({ block: 'center' }) : undefined}
                    style={{ display: 'block', width: '100%', textAlign: 'left', padding: '10px 16px', border: 'none', borderBottom: '1px solid #f1f5f9', cursor: 'pointer', minHeight: 48, background: selected ? '#dbeafe' : 'white' }}>
                    <span style={{ display: 'block', fontSize: 15, fontWeight: selected ? 700 : 600, color: '#111827' }}>{formatCounterLabel(n, operationMode)}</span>
                    {cv && (
                      <span style={{ display: 'block', fontSize: 12, color: vacancyColour(cv), marginTop: 1 }}>
                        {cv.state === 'free' ? '✓ ' : '✕ '}{vacancyText(cv, slotTime, NUM_SLOTS)}
                      </span>
                    )}
                    {short && (
                      <span style={{ display: 'block', fontSize: 11, color: AMBER, marginTop: 1 }}>
                        Taken before this assignment ends ({slotTime(neededUntilSlot!)})
                      </span>
                    )}
                  </button>
                );
              })}
              {duties.length > 0 && (
                <>
                  <div style={{ ...GROUP_HEADER_LOOK, padding: '6px 16px', fontSize: 12 }}>{DUTY_GROUP_LABEL}</div>
                  {duties.map((d) => {
                    const selected = d === currentDuty;
                    return (
                      <button key={d} type="button" onClick={() => pick(dutyChoice(d))}
                        style={{ display: 'block', width: '100%', textAlign: 'left', padding: '10px 16px', border: 'none', borderBottom: '1px solid #e2e5ea', cursor: 'pointer', minHeight: 44, background: DUTY_FILL, outline: selected ? '2px solid #2563eb' : 'none', outlineOffset: -2 }}>
                        <span style={{ fontSize: 15, fontStyle: 'italic', fontWeight: selected ? 700 : 500, color: DUTY_INK }}>{d}</span>
                      </button>
                    );
                  })}
                </>
              )}
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}

export const CounterChoiceSelect = forwardRef<HTMLSelectElement, Props>(function CounterChoiceSelect(
  { value, onChange, numCounters, operationMode, disabled, emptyLabel, style, vacancyOf, slotTime, neededUntilSlot, title, ...rest }, ref,
) {
  const listed = useDutyOptions().map((d) => d.text);
  const current = parseCounterChoice(value).duty;
  const duties = dutiesToOffer(listed, current);
  const waiting = value === '';

  if (vacancyOf && slotTime) {
    return (
      <CounterChoiceSheet
        value={value} onChange={onChange} numCounters={numCounters} operationMode={operationMode}
        disabled={disabled} emptyLabel={emptyLabel} style={style}
        vacancyOf={vacancyOf} slotTime={slotTime} neededUntilSlot={neededUntilSlot} title={title}
      />
    );
  }

  return (
    <select
      ref={ref}
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      style={{
        ...style,
        ...(current !== undefined ? DUTY_LOOK : {}),
        ...(waiting && !disabled ? EMPTY_CHOICE_LOOK : {}),
      }}
      {...rest}
    >
      {waiting && <option value="" disabled>{emptyLabel ?? 'Select ctr'}</option>}
      <optgroup label={COUNTER_GROUP_LABEL} style={GROUP_HEADER_LOOK}>
        {Array.from({ length: numCounters }, (_, i) => (
          <option key={i + 1} value={String(i + 1)} style={{ fontStyle: 'normal' }}>
            {formatCounterLabel(i + 1, operationMode)}
          </option>
        ))}
      </optgroup>
      {duties.length > 0 && (
        <optgroup label={DUTY_GROUP_LABEL} style={GROUP_HEADER_LOOK}>
          {duties.map((d) => (
            <option key={d} value={dutyChoice(d)} style={{ fontStyle: 'italic', background: 'white', color: 'black', fontWeight: 400 }}>
              {d}
            </option>
          ))}
        </optgroup>
      )}
    </select>
  );
});
